import React from 'react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Line, LineChart, Pie, PieChart,
  PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Scatter, ScatterChart,
  Tooltip, XAxis, YAxis, ZAxis,
} from 'recharts';
import type { ValueFormat } from './export/bundle';
import { formatValue } from './export/bundle';
import type { ChartData } from './engine';
import type { ChartType } from './model';

/** A chart of the studio, drawn from what the engine worked out — the same numbers the PDF and Excel get. */
export const ChartView: React.FC<{ type: ChartType; data: ChartData; locale: string; showLabels?: boolean; empty: string }> = ({
  type, data, locale, showLabels, empty,
}) => {
  const fmt = (v: unknown) => (typeof v === 'number' ? formatValue(v, data.format, locale) : String(v ?? ''));
  const axisFmt = (v: unknown) => (typeof v === 'number' ? short(v, data.format, locale) : String(v ?? ''));

  if (type === 'kpi') {
    const k = data.kpi;
    return (
      <div className="h-full w-full flex flex-col items-center justify-center gap-1 text-center px-2">
        <div className="text-[11px] uppercase tracking-wide text-gn-secondary">{k?.label}</div>
        <div className="font-semibold text-gn-primary" style={{ fontSize: 'clamp(20px, 6vw, 44px)' }}>
          {k && k.value != null ? formatValue(k.value, k.format, locale) : '—'}
        </div>
      </div>
    );
  }

  if (data.series.length === 0 || data.categories.length === 0) {
    return <div className="h-full w-full flex items-center justify-center text-[11px] text-gn-muted">{empty}</div>;
  }

  const rows = data.categories.map((c, i) => {
    const row: Record<string, string | number | null> = { __cat: c };
    data.series.forEach((s, si) => { row[`s${si}`] = s.values[i]; });
    return row;
  });
  const legend = data.series.length > 1;
  const grid = <CartesianGrid strokeDasharray="3 3" stroke="var(--gn-border)" />;
  const tooltip = <Tooltip formatter={(v: unknown) => fmt(v)} contentStyle={{ fontSize: 11 }} />;
  const tick = { fontSize: 10, fill: 'var(--gn-text-secondary)' };
  const labels = (si: number) => (showLabels ? <LabelList dataKey={`s${si}`} position="top" formatter={(v: unknown) => axisFmt(v)} style={{ fontSize: 9, fill: 'var(--gn-text-secondary)' }} /> : null);

  switch (type) {
    case 'column':
    case 'stackedColumn':
    case 'percentColumn':
    case 'bar':
    case 'stackedBar': {
      const horizontal = type === 'bar' || type === 'stackedBar';
      const stacked = type === 'stackedColumn' || type === 'stackedBar' || type === 'percentColumn';
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout={horizontal ? 'vertical' : 'horizontal'} stackOffset={type === 'percentColumn' ? 'expand' : undefined}
                    margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            {grid}
            {horizontal ? (
              <>
                <XAxis type="number" tick={tick} tickFormatter={axisFmt} />
                <YAxis type="category" dataKey="__cat" tick={tick} width={90} />
              </>
            ) : (
              <>
                <XAxis dataKey="__cat" tick={tick} interval={0} angle={rows.length > 6 ? -30 : 0} textAnchor={rows.length > 6 ? 'end' : 'middle'} height={rows.length > 6 ? 50 : 24} />
                <YAxis tick={tick} tickFormatter={type === 'percentColumn' ? (v: number) => `${Math.round(v * 100)}%` : axisFmt} width={52} />
              </>
            )}
            {tooltip}
            {legend && <Legend wrapperStyle={{ fontSize: 10 }} />}
            {data.series.map((s, si) => (
              <Bar key={si} dataKey={`s${si}`} name={s.name} fill={s.color} stackId={stacked ? 'a' : undefined} isAnimationActive={false}>
                {!stacked && labels(si)}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }
    case 'line':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            {grid}
            <XAxis dataKey="__cat" tick={tick} />
            <YAxis tick={tick} tickFormatter={axisFmt} width={52} />
            {tooltip}
            {legend && <Legend wrapperStyle={{ fontSize: 10 }} />}
            {data.series.map((s, si) => (
              <Line key={si} dataKey={`s${si}`} name={s.name} stroke={s.color} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} isAnimationActive={false}>
                {labels(si)}
              </Line>
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    case 'area':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            {grid}
            <XAxis dataKey="__cat" tick={tick} />
            <YAxis tick={tick} tickFormatter={axisFmt} width={52} />
            {tooltip}
            {legend && <Legend wrapperStyle={{ fontSize: 10 }} />}
            {data.series.map((s, si) => (
              <Area key={si} dataKey={`s${si}`} name={s.name} stroke={s.color} fill={s.color} fillOpacity={0.25} isAnimationActive={false} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );
    case 'pie':
    case 'donut': {
      const s = data.series[0];
      const slices = data.categories.map((c, i) => ({ name: c, value: s.values[i] ?? 0 })).filter((x) => x.value > 0);
      const PIE = ['#2563eb', '#e11d48', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#4d7c0f', '#ea580c', '#4f46e5', '#0d9488', '#9333ea'];
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="name" innerRadius={type === 'donut' ? '45%' : 0} outerRadius="75%"
                 label={showLabels ? (e: { value: number }) => axisFmt(e.value) : false} isAnimationActive={false}>
              {slices.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
            </Pie>
            {tooltip}
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    case 'radar':
      return (
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={rows} outerRadius="70%">
            <PolarGrid stroke="var(--gn-border)" />
            <PolarAngleAxis dataKey="__cat" tick={{ fontSize: 9, fill: 'var(--gn-text-secondary)' }} />
            <PolarRadiusAxis tick={{ fontSize: 8 }} tickFormatter={axisFmt} />
            {tooltip}
            {legend && <Legend wrapperStyle={{ fontSize: 10 }} />}
            {data.series.map((s, si) => (
              <Radar key={si} dataKey={`s${si}`} name={s.name} stroke={s.color} fill={s.color} fillOpacity={0.2} isAnimationActive={false} />
            ))}
          </RadarChart>
        </ResponsiveContainer>
      );
    case 'scatter': {
      const xs = data.scatterX ?? [];
      return (
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            {grid}
            <XAxis type="number" dataKey="x" name={data.categoryLabel} tick={tick} tickFormatter={(v: number) => short(v, 'number', locale)} />
            <YAxis type="number" dataKey="y" tick={tick} tickFormatter={axisFmt} width={52} />
            <ZAxis range={[40, 40]} />
            {tooltip}
            {legend && <Legend wrapperStyle={{ fontSize: 10 }} />}
            {data.series.map((s, si) => (
              <Scatter key={si} name={s.name} fill={s.color} isAnimationActive={false}
                       data={data.categories.map((c, i) => ({ x: xs[i], y: s.values[i], c })).filter((p) => p.x != null && p.y != null)} />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      );
    }
    default:
      return null;
  }
};

function short(v: number, format: ValueFormat, locale: string): string {
  const a = Math.abs(v);
  if (format !== 'percent' && a >= 1e6) return (v / 1e6).toLocaleString(locale, { maximumFractionDigits: 1 }) + 'M';
  if (format !== 'percent' && a >= 1e4) return (v / 1e3).toLocaleString(locale, { maximumFractionDigits: 0 }) + 'k';
  return formatValue(v, format === 'integer' ? 'integer' : format, locale);
}

/* ───────────── chart gallery: pick the chart by its picture ───────────── */

const C = ['#2563eb', '#e11d48', '#16a34a', '#d97706'];

export const CHART_PICTURES: Record<ChartType, React.ReactNode> = {
  column: (
    <svg viewBox="0 0 40 28"><rect x="4" y="12" width="6" height="14" fill={C[0]} /><rect x="13" y="6" width="6" height="20" fill={C[0]} /><rect x="22" y="15" width="6" height="11" fill={C[0]} /><rect x="31" y="9" width="6" height="17" fill={C[0]} /></svg>
  ),
  stackedColumn: (
    <svg viewBox="0 0 40 28"><rect x="5" y="14" width="7" height="12" fill={C[0]} /><rect x="5" y="8" width="7" height="6" fill={C[1]} /><rect x="16" y="10" width="7" height="16" fill={C[0]} /><rect x="16" y="3" width="7" height="7" fill={C[1]} /><rect x="27" y="16" width="7" height="10" fill={C[0]} /><rect x="27" y="10" width="7" height="6" fill={C[1]} /></svg>
  ),
  percentColumn: (
    <svg viewBox="0 0 40 28"><rect x="5" y="12" width="7" height="14" fill={C[0]} /><rect x="5" y="2" width="7" height="10" fill={C[1]} /><rect x="16" y="8" width="7" height="18" fill={C[0]} /><rect x="16" y="2" width="7" height="6" fill={C[1]} /><rect x="27" y="16" width="7" height="10" fill={C[0]} /><rect x="27" y="2" width="7" height="14" fill={C[1]} /></svg>
  ),
  bar: (
    <svg viewBox="0 0 40 28"><rect x="3" y="3" width="26" height="5" fill={C[0]} /><rect x="3" y="11" width="34" height="5" fill={C[0]} /><rect x="3" y="19" width="18" height="5" fill={C[0]} /></svg>
  ),
  stackedBar: (
    <svg viewBox="0 0 40 28"><rect x="3" y="3" width="16" height="5" fill={C[0]} /><rect x="19" y="3" width="10" height="5" fill={C[1]} /><rect x="3" y="11" width="22" height="5" fill={C[0]} /><rect x="25" y="11" width="12" height="5" fill={C[1]} /><rect x="3" y="19" width="10" height="5" fill={C[0]} /><rect x="13" y="19" width="9" height="5" fill={C[1]} /></svg>
  ),
  line: (
    <svg viewBox="0 0 40 28"><polyline points="3,22 12,14 20,17 28,7 37,10" fill="none" stroke={C[0]} strokeWidth="2" /><polyline points="3,25 12,20 20,22 28,16 37,18" fill="none" stroke={C[1]} strokeWidth="2" /></svg>
  ),
  area: (
    <svg viewBox="0 0 40 28"><polygon points="3,26 3,18 12,10 20,14 28,5 37,9 37,26" fill={C[0]} opacity="0.5" /><polyline points="3,18 12,10 20,14 28,5 37,9" fill="none" stroke={C[0]} strokeWidth="1.5" /></svg>
  ),
  pie: (
    <svg viewBox="0 0 40 28"><circle cx="20" cy="14" r="11" fill={C[0]} /><path d="M20 14 L20 3 A11 11 0 0 1 30.5 17.5 Z" fill={C[1]} /><path d="M20 14 L30.5 17.5 A11 11 0 0 1 14 23.5 Z" fill={C[2]} /></svg>
  ),
  donut: (
    <svg viewBox="0 0 40 28"><circle cx="20" cy="14" r="11" fill={C[0]} /><path d="M20 14 L20 3 A11 11 0 0 1 30.5 17.5 Z" fill={C[1]} /><path d="M20 14 L30.5 17.5 A11 11 0 0 1 14 23.5 Z" fill={C[2]} /><circle cx="20" cy="14" r="5.5" fill="var(--gn-bg-secondary)" /></svg>
  ),
  scatter: (
    <svg viewBox="0 0 40 28">{[[6, 20], [10, 16], [14, 18], [18, 11], [23, 13], [27, 7], [31, 9], [35, 4]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2" fill={C[i % 2]} />)}</svg>
  ),
  radar: (
    <svg viewBox="0 0 40 28"><polygon points="20,2 32,10 28,25 12,25 8,10" fill="none" stroke="var(--gn-border)" /><polygon points="20,6 29,11 24,21 15,22 12,11" fill={C[0]} opacity="0.5" stroke={C[0]} /></svg>
  ),
  kpi: (
    <svg viewBox="0 0 40 28"><rect x="3" y="4" width="34" height="20" rx="3" fill="none" stroke={C[0]} strokeWidth="1.5" /><text x="20" y="18.5" textAnchor="middle" fontSize="10" fontWeight="bold" fill={C[0]}>123</text></svg>
  ),
};

export const CHART_ORDER: ChartType[] = [
  'column', 'stackedColumn', 'percentColumn', 'bar', 'stackedBar', 'line', 'area', 'pie', 'donut', 'scatter', 'radar', 'kpi',
];
